/**
 * IDB CRUD operations для `projects` store.
 *
 * Used by Phase B+ (Calculator UI):
 *   - saveProject() — debounced auto-save при изменении формы
 *   - getProject() — на mount экрана анкеты (восстановление draft)
 *   - listProjects() — на mount /calculator (Entry screen с draft баннером)
 *
 * syncStatus state machine (ADR-0019 §Q11.4):
 *   - 'local' — created offline, никогда не sync'илось.
 *   - 'pending' — sync attempt in flight (BackgroundSync queue picked up).
 *   - 'synced' — server confirmed save.
 *
 * Indexes:
 *   - by-userId (для filter в Phase 11 ЛК — список своих проектов)
 *   - by-syncStatus (для sync queue scan)
 *
 * References: ADR-0019 §Q11.2, §Q11.3 (local UUID), §Q11.4 (sync states)
 */

import type { CalculatorAnketaType } from '@pomoshnik/shared';

import { openDb, type ProjectRecord, type SyncStatus } from './database';

export async function saveProject(record: ProjectRecord): Promise<void> {
  const db = await openDb();
  await db.put('projects', record);
}

export async function getProject(id: string): Promise<ProjectRecord | undefined> {
  const db = await openDb();
  return db.get('projects', id);
}

export async function deleteProject(id: string): Promise<void> {
  const db = await openDb();
  await db.delete('projects', id);
}

export async function listProjectsByUser(userId: string): Promise<ProjectRecord[]> {
  const db = await openDb();
  return db.getAllFromIndex('projects', 'by-userId', userId);
}

export async function listProjectsBySyncStatus(syncStatus: SyncStatus): Promise<ProjectRecord[]> {
  const db = await openDb();
  return db.getAllFromIndex('projects', 'by-syncStatus', syncStatus);
}

export async function listAllProjects(): Promise<ProjectRecord[]> {
  const db = await openDb();
  return db.getAll('projects');
}

/** Update only syncStatus field (atomic transition pending → synced). */
export async function updateProjectSyncStatus(id: string, syncStatus: SyncStatus): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('projects', 'readwrite');
  const existing = await tx.store.get(id);
  if (!existing) {
    await tx.done;
    throw new Error(`PROJECT_NOT_FOUND: id=${id}`);
  }
  await tx.store.put({ ...existing, syncStatus });
  await tx.done;
}

/** Bulk count для UI badge (e.g., OnlineIndicator pending count). */
export async function countProjectsBySyncStatus(syncStatus: SyncStatus): Promise<number> {
  const db = await openDb();
  return db.countFromIndex('projects', 'by-syncStatus', syncStatus);
}

/**
 * PR-H3 (ADR-0028) — список projects, отфильтрованных по anketaType.
 *
 * `ProjectRecord.furnitureType` хранит anketaType-key (см. `useAnketaPersist.ts:211`),
 * поэтому фильтрация по равенству anketaType корректна. Использует in-memory filter
 * поверх `listAllProjects()` — separate index по furnitureType избыточен для drafts
 * list (small dataset, single-user device).
 *
 * Returns sorted by `updatedAt` DESC (most recent first) — для UI drafts list
 * ordering на CalculatorEntry.
 */
export async function listProjectsByAnketaType(
  anketaType: CalculatorAnketaType,
): Promise<ProjectRecord[]> {
  const all = await listAllProjects();
  return all
    .filter((p) => p.furnitureType === anketaType)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * PR-H3 (ADR-0028) — auto-name generator per anketaType.
 *
 * Возвращает «{typeLabel} №{N}», где N = `max(existing trailing №\d+ в name) + 1`.
 * Если нет existing projects этого типа с matching pattern → N = 1.
 *
 * Counter strategy (ADR-0028 Q1 outcome, max+1): gaps при удалении НЕ переиспользуются.
 * Legacy projects без «№N» pattern (например `'(без названия)'` placeholder из
 * `useAnketaPersist.stateToRecord`) игнорируются в counter — их `name` отображается
 * с fallback в drafts list (Q2 outcome).
 *
 * @example
 *   generateProjectName('kitchen', 'Кухня')  // → 'Кухня №1' (store пуст)
 *   // С existing «Кухня №1», «Кухня №3», «(без названия)»:
 *   generateProjectName('kitchen', 'Кухня')  // → 'Кухня №4' (max+1, gap при №2 не recycled)
 */
export async function generateProjectName(
  anketaType: CalculatorAnketaType,
  typeLabel: string,
): Promise<string> {
  const existing = await listProjectsByAnketaType(anketaType);
  const numbers: number[] = [];
  for (const p of existing) {
    // `.trim()` defensive — пользователь может ввести trailing whitespace или
    // NBSP вручную в name; regex `/№(\d+)$/` anchored к end строки без trim
    // не сматчит и counter ошибочно начнётся с № заново → potential duplicates.
    const match = p.name.trim().match(/№(\d+)$/);
    if (match !== null) {
      numbers.push(Number(match[1]));
    }
  }
  const maxNum = numbers.length > 0 ? Math.max(...numbers) : 0;
  return `${typeLabel} №${maxNum + 1}`;
}
